/* PoDs by delivery date: for each day (or week, on ranges over 31 days) the shipments delivered, split into PoD received
   and PoD missing. One axis (shipments); the % with PoD and whether it met the target is in the tooltip and the sentence
   above the chart, which names the worst period and how many periods missed the target.
   Below 4 periods with deliveries a chart says little, so the card falls back to a one-line summary. */
import React, { useMemo } from 'react'
import { addDays, format, parseISO, startOfWeek } from 'date-fns'
import { Box, Card, CardContent, Typography, useTheme } from '@mui/material'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { TONE, num } from './podUtils'

const WEEKLY_OVER_DAYS = 31

/** Daily points → chart periods (daily, or Monday-based weeks over 31 days), with % received and target status. */
export const toPeriods = (trend, target) => {
  const weekly = trend.length > WEEKLY_OVER_DAYS
  const map = new Map()
  trend.forEach((p) => {
    const d = parseISO(p.date)
    const start = weekly ? startOfWeek(d, { weekStartsOn: 1 }) : d
    const key = format(start, 'yyyy-MM-dd')
    if (!map.has(key)) {
      map.set(key, { key, start, delivered: 0, received: 0, pending: 0,
        label: format(start, 'dd MMM'), title: weekly ? `Week of ${format(start, 'dd MMM')}` : format(start, 'EEE, dd MMM yyyy'),
        range: weekly ? `${format(start, 'dd MMM')} – ${format(addDays(start, 6), 'dd MMM')}` : null })
    }
    const g = map.get(key)
    g.delivered += p.delivered
    g.received += p.received
    g.pending += p.pending
  })
  const rows = [...map.values()]
  rows.forEach((g) => {
    g.pct = g.delivered ? Math.round((1000 * g.received) / g.delivered) / 10 : null
    g.met = g.pct == null ? null : g.pct >= target
  })
  return { weekly, rows }
}

function PeriodTooltip({ active, payload, target, C }) {
  if (!active || !payload?.length) return null
  const p = payload[0].payload
  return (
    <Box sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1.5, p: 1.25, boxShadow: 2, fontSize: 12, minWidth: 190 }}>
      <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{p.title}</Typography>
      {p.range && <Typography sx={{ fontSize: 11, color: 'text.secondary', mb: 0.5 }}>{p.range}</Typography>}
      {p.delivered === 0 ? <div>No PTL shipments delivered.</div> : (
        <>
          <div>{num(p.delivered)} delivered</div>
          <div style={{ color: C.received }}>{num(p.received)} with PoD</div>
          <div style={{ color: C.missing }}>{num(p.pending)} PoD missing</div>
          <div style={{ marginTop: 4, fontWeight: 700, color: p.met ? C.received : C.missing }}>
            {p.pct}% with PoD: {p.met ? 'target met' : `below the ${target}% target`}
          </div>
        </>
      )}
    </Box>
  )
}

const LegendItem = ({ swatch, label }) => (
  <Box component='li' sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: 12, color: 'text.secondary' }}>
    {swatch}<span>{label}</span>
  </Box>
)

export default function PodTrendCard({ summary }) {
  const theme = useTheme()
  // Theme tokens, not raw hex: received = success, missing = error.
  const C = { received: theme.palette.success.main, missing: theme.palette.error.main,
    grid: theme.palette.grey[200], axis: theme.palette.text.secondary }
  const target = summary?.receivedTargetPct ?? 95
  const { weekly, rows } = useMemo(() => toPeriods(summary?.trend || [], target), [summary, target])
  const active = rows.filter((r) => r.delivered > 0)
  const below = active.filter((r) => !r.met)
  const worst = [...below].sort((a, b) => a.pct - b.pct || b.pending - a.pending)[0]
  const unit = weekly ? 'week' : 'day'

  return (
    <Card elevation={0} sx={{ border: 1, borderColor: 'divider', height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
          <Typography component='h2' sx={{ fontWeight: 700, fontSize: 16 }}>PoDs by delivery date</Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{weekly ? 'Weekly (range over 31 days)' : 'Daily'}</Typography>
        </Box>

        {summary && active.length >= 4 && (
          <Typography sx={{ fontSize: 13, mt: 0.5, mb: 1.5 }}>
            {below.length === 0
              ? <>Every {unit} with deliveries met the {target}% target.</>
              : <>
                  <b>{below.length} of {active.length} {unit}s</b> with deliveries were below the {target}% target.
                  {' '}Worst: <b>{worst.title}</b>, {worst.pct}% with PoD ({num(worst.pending)} of {num(worst.delivered)} missing).
                </>}
          </Typography>
        )}

        {summary && active.length < 4 ? (
          <Box sx={{ p: 2, mt: 1, borderRadius: 2, bgcolor: TONE.neutral.bg, color: 'text.secondary', fontSize: 13 }}>
            Deliveries on only {active.length} {active.length === 1 ? unit : `${unit}s`} in these dates, too few for a trend. Pick a longer range to see one.
          </Box>
        ) : (
          <>
            <Box sx={{ height: 220, minWidth: 280 }} role='img'
              aria-label={`PoDs by delivery ${unit}: ${below.length} of ${active.length} ${unit}s below the ${target}% target`}>
              <ResponsiveContainer width='100%' height='100%'>
                <BarChart data={rows} margin={{ top: 8, right: 4, left: 0, bottom: 0 }} barCategoryGap='25%'>
                  <CartesianGrid stroke={C.grid} vertical={false} />
                  <XAxis dataKey='label' tick={{ fontSize: 11, fill: C.axis }} tickLine={false} axisLine={{ stroke: C.grid }} minTickGap={18} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: C.axis }} axisLine={false} tickLine={false} width={40} tickFormatter={(n) => num(n)} />
                  <Tooltip content={<PeriodTooltip target={target} C={C} />} cursor={{ fill: 'rgba(32,101,209,.06)' }} />
                  <Bar dataKey='received' name='With PoD' stackId='d' fill={C.received} stroke={theme.palette.background.paper} strokeWidth={1} maxBarSize={weekly ? 44 : 24} />
                  <Bar dataKey='pending' name='PoD missing' stackId='d' fill={C.missing} radius={[4, 4, 0, 0]} maxBarSize={weekly ? 44 : 24} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
            <Box component='ul' sx={{ listStyle: 'none', m: 0, mt: 1, p: 0, display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
              <LegendItem swatch={<Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: C.received }} />} label='PoD received' />
              <LegendItem swatch={<Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: C.missing }} />} label='PoD missing' />
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  )
}
